/**
 * useCalendarFilters — the Calendars list's state.
 *
 * PLANNER-2. The artboard's left rail has a Calendars panel: a
 * checkbox, a name, a count for the month on show, and a coloured dot
 * you click to recolour. Asked what the rows should actually be in
 * Orbit, Rennan said both — what a thing IS and where it came FROM —
 * so there are two groups and they are different in kind:
 *
 *   TYPES are what the calendar draws. My blocks, Bookings, Due dates,
 *   Milestones. Each owns a colour, and that colour is what the month
 *   chips and the hour-grid blocks are painted in.
 *
 *   CONNECTED is where a row came from. Orbit, Google, iCloud,
 *   Microsoft. These carry no colour, deliberately: a source is a
 *   filter, not a hue, and giving them dots would imply you could
 *   colour by two things at once.
 *
 * The two are AND-ed. Unticking Bookings hides every booking;
 * unticking Google hides everything Google sent, whatever type it is.
 *
 * Only sources that are actually present are listed, plus Orbit, which
 * is always the studio's own. A row reading "Microsoft 0" on an
 * account with no Microsoft calendar is a row that only ever says no.
 */
import { useCallback, useMemo, useState } from 'react';
import { isSameMonth } from 'date-fns';
import type { DetailedCalendarEvent } from '../calendar/EventDetailsModal';

export type EventType = DetailedCalendarEvent['type'];
export type EventSource = DetailedCalendarEvent['source'];

/** The four things the calendar draws, in the order the rail lists them. */
export const TYPE_ROWS: { id: EventType; name: string; color: string }[] = [
  // Mirrored by hand on iOS — a change here has to happen there too.
  { id: 'personal', name: 'My blocks', color: '#2D6E70' },
  { id: 'booking', name: 'Bookings', color: '#3B82F6' },
  { id: 'due_date', name: 'Due dates', color: '#DB4433' },
  { id: 'milestone', name: 'Milestones', color: '#E8C547' },
];

const SOURCE_NAMES: Record<EventSource, string> = {
  orbit: 'Orbit',
  google: 'Google',
  apple: 'iCloud',
  microsoft: 'Microsoft',
  // A booking is its own source in the stream, but in this panel it is
  // a TYPE, not a place events arrive from. Never listed under
  // Connected; the Bookings row above covers it.
  booking: 'Bookings',
};
const CONNECTED_ORDER: EventSource[] = ['orbit', 'google', 'apple', 'microsoft'];

/** The artboard's own swatch cycle. */
export const PALETTE = [
  '#4F46E5',
  '#0369A1',
  '#0F766E',
  '#15803D',
  '#B45309',
  '#C2410C',
  '#BE185D',
  '#7C3AED',
];

const KEY = 'orbit.planner.calendars';

interface Stored {
  hiddenTypes?: Record<string, boolean>;
  hiddenSources?: Record<string, boolean>;
  colors?: Record<string, string>;
}

const load = (): Stored => {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    // Unreadable or not JSON — start from defaults rather than throw.
    return {};
  }
};

export interface CalendarRow {
  id: string;
  name: string;
  on: boolean;
  count: number;
  /** Types carry a colour; sources do not. */
  color?: string;
  toggle: () => void;
  cycle?: () => void;
}

export function useCalendarFilters(events: DetailedCalendarEvent[], month: Date) {
  const [stored, setStored] = useState<Stored>(load);

  const save = useCallback((next: Stored) => {
    setStored(next);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Private mode. Not remembering is survivable.
    }
  }, []);

  // Memoised, not defaulted inline. `stored.hiddenTypes ?? {}` makes a
  // NEW object every render, which makes `visible` a new array every
  // render, which makes the grid's `columns` memo re-run `layoutDay`
  // for all seven days on every render in the tree. The empty object
  // is the whole reason: a studio who has hidden nothing is the common
  // case and was the slow one.
  const hiddenTypes = useMemo(() => stored.hiddenTypes ?? {}, [stored.hiddenTypes]);
  const hiddenSources = useMemo(() => stored.hiddenSources ?? {}, [stored.hiddenSources]);
  const colors = useMemo(() => stored.colors ?? {}, [stored.colors]);

  const colorOf = useCallback(
    (e: DetailedCalendarEvent) =>
      colors[e.type] ?? TYPE_ROWS.find((t) => t.id === e.type)?.color ?? '#6E6E73',
    [colors],
  );

  const visible = useMemo(
    () => events.filter((e) => !hiddenTypes[e.type] && !hiddenSources[e.source]),
    [events, hiddenSources, hiddenTypes],
  );

  /** Which sources this studio actually has. Orbit is always one. */
  const presentSources = useMemo(() => {
    const seen = new Set<EventSource>(['orbit']);
    for (const e of events) if (e.source !== 'booking') seen.add(e.source);
    return CONNECTED_ORDER.filter((s) => seen.has(s));
  }, [events]);

  /**
   * Counts are for the month ON SHOW, as the artboard has them, and
   * they count what the filter would show if that one row were on —
   * so a row never reads 0 just because it is unticked.
   */
  const inMonth = useMemo(
    () => events.filter((e) => isSameMonth(e.startAt, month)),
    [events, month],
  );

  const typeRows: CalendarRow[] = TYPE_ROWS.map((t) => {
    const on = !hiddenTypes[t.id];
    return {
      id: t.id,
      name: t.name,
      on,
      color: colors[t.id] ?? t.color,
      count: inMonth.filter((e) => e.type === t.id && !hiddenSources[e.source]).length,
      toggle: () => save({ ...stored, hiddenTypes: { ...hiddenTypes, [t.id]: on } }),
      cycle: () => {
        const current = colors[t.id] ?? t.color;
        const at = PALETTE.indexOf(current);
        save({ ...stored, colors: { ...colors, [t.id]: PALETTE[(at + 1) % PALETTE.length] } });
      },
    };
  });

  const sourceRows: CalendarRow[] = presentSources.map((s) => {
    const on = !hiddenSources[s];
    return {
      id: s,
      name: SOURCE_NAMES[s],
      on,
      count: inMonth.filter((e) => e.source === s && !hiddenTypes[e.type]).length,
      toggle: () => save({ ...stored, hiddenSources: { ...hiddenSources, [s]: on } }),
    };
  });

  return { visible, colorOf, typeRows, sourceRows };
}

export default useCalendarFilters;
